// Pure helpers that let the flyover share wizard serve TWO subjects: a recording (the original 3D
// flyover) and a hike (the Trail Trailer). Kept out of the Vue component so the branching — which
// mutation to call, what its input looks like, where the poll finds its videos — is unit-testable.

import { normalizeStyleUrl, ASPECT_VERTICAL, ASPECT_HORIZONTAL } from './shareActions';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';

export interface ShareSubject {
  recordingId?: string | null;
  hikeId?: string | null;
}

/** A config opened for a hike (Trail Trailer) rather than a recording (flyover). */
export function isHikeShare(cfg: ShareSubject | null | undefined): boolean {
  return !!cfg?.hikeId && String(cfg.hikeId).trim() !== '';
}

export type GenerateKind = 'hikeTrailer' | 'recordingFlyover';

export interface GenerateRequest {
  kind: GenerateKind;
  input: Record<string, unknown>;
}

/**
 * The generate-mutation input for the chosen subject and format. A trailer has no speed selector —
 * it runs a fixed body duration by design — so speed is only sent on the recording path. The style is
 * normalised the same way on both, so generation and the server's cached-render reuse line up.
 */
export function buildGenerateRequest(
  cfg: ShareSubject,
  format: 'vertical' | 'horizontal',
  mapStyleUrl?: string | null,
): GenerateRequest {
  const aspect = format === 'vertical' ? ASPECT_VERTICAL : ASPECT_HORIZONTAL;
  if (isHikeShare(cfg)) {
    return {
      kind: 'hikeTrailer',
      input: { hikeId: String(cfg.hikeId), aspect, mapStyleUrl: normalizeStyleUrl(mapStyleUrl) },
    };
  }
  return {
    kind: 'recordingFlyover',
    input: {
      recordingId: String(cfg.recordingId ?? ''),
      aspect,
      speed: 1,
      mapStyleUrl: normalizeStyleUrl(mapStyleUrl),
    },
  };
}

/** Pull the poll's video list out of either query's response shape. */
export function videosFromPollData(data: unknown, kind: GenerateKind): unknown[] {
  const d = (data ?? {}) as Record<string, any>;
  const list = kind === 'hikeTrailer' ? d.hikeTrailerVideos : d.recording?.flyoverVideos;
  return Array.isArray(list) ? list : [];
}

/** The hike fields the share wizard's stat row is built from (as GetHike returns them). */
export interface HikeShareStatSource {
  distanceMeters?: number | string | null;
  elevationGainMeters?: number | string | null;
  // Either shape: the flyover's GetHike carries the minutes (compacted below), while the hike list's
  // rows carry the server's ready-made display string. Whichever is present is used.
  durationMinutes?: number | string | null;
  durationDisplay?: string | null;
}

/**
 * A walking estimate as a SHORT string: '45m', '4h 21m', '5h', '3d 4h'.
 *
 * Short is the requirement, not a preference. The wizard lays its three stats out in one row, so a
 * long value wraps mid-string and drags its label out of line with the other two — which is what made
 * the row look ragged. The server's own strings are too long for it: a hike's `durationDisplay` reads
 * "~4 hrs 21 min" and a recording's `formatted_time_string` reads "4h 21m 13.0s". Seconds are noise on
 * a multi-hour estimate, so they are dropped, and a zero remainder is omitted rather than padded ('5h',
 * not '5h 0m'). Minutes come from `durationMinutes`, which the server has already folded the viewer's
 * hike-time preference into.
 */
export function compactDurationFromMinutes(minutes: number | string | null | undefined): string {
  const total = Math.round(Number(minutes));
  if (!Number.isFinite(total) || total <= 0) return '';
  if (total < 60) return `${total}m`;

  const hours = Math.floor(total / 60);
  if (hours < 24) {
    const rest = total % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days}d` : `${days}d ${restHours}h`;
}

export interface HikeShareStats {
  distance: string;
  elevation: string;
  duration: string;
}

/**
 * The three preformatted stats the share wizard shows for a hike — the counterpart of the data-*
 * attributes a recording's page renders server-side (see recordingShareConfig in entrypoints).
 *
 * A hike had none: buildHikeShareConfig only ever returned id/title/shareUrl, so the wizard fell back
 * to its em-dash placeholder for all three and the popup read as empty. Derived from the hike GetHike
 * already fetched rather than from page globals, so it works from every entry point that can open the
 * flyover, not just the hike page.
 *
 * Distance and elevation go through MeasurementPrefUtility — the same conversion the flyover overlay's
 * live readout uses, so the wizard agrees with the clip playing behind it. `convertToSmallUnitBefore1000M`
 * mirrors the recording page's `convert_meter_to(m, true)`, so a sub-kilometre hike reads in metres.
 * Duration is shortened from the server's viewer-paced `durationMinutes` — see
 * compactDurationFromMinutes for why the row cannot take a long one.
 *
 * A missing or non-positive value yields '' rather than '0 km', so the wizard keeps showing its
 * placeholder instead of asserting a stat the hike does not have.
 */
export function hikeShareStats(hike: HikeShareStatSource | null | undefined): HikeShareStats {
  const units = MeasurementPrefUtility.preference();
  const meters = (value: unknown): number | null => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const distanceMeters = meters(hike?.distanceMeters);
  const elevationMeters = meters(hike?.elevationGainMeters);
  return {
    distance: distanceMeters == null ? '' : units.bigUnitFormatter(distanceMeters / 1000, true),
    elevation: elevationMeters == null ? '' : units.smallUnitFormatter(elevationMeters),
    duration: compactDurationFromMinutes(hike?.durationMinutes) || (hike?.durationDisplay || '').trim(),
  };
}

export interface HikePageGlobals {
  hike_id?: string | number | null;
  trail_name?: string | null;
  hike_name?: string | null;
}

/**
 * The share-wizard config for a hike's Trail Trailer, built from the hike page's window globals
 * (hikes/show sets window.hike_id and window.trail_name). shareUrl is the page itself — the hike IS
 * the shareable thing. Returns null when the page carries no hike id, so the caller simply doesn't
 * offer the button.
 */
export function buildHikeShareConfig(
  globals: HikePageGlobals | null | undefined,
  pageUrl: string,
  mapStyleUrl?: string | null,
  stats?: HikeShareStats | null,
): {
  hikeId: string;
  title: string;
  shareUrl: string;
  mapStyleUrl?: string;
  distance?: string;
  elevation?: string;
  duration?: string;
} | null {
  const id = globals?.hike_id != null ? String(globals.hike_id).trim() : '';
  if (!id) return null;
  return {
    hikeId: id,
    title: (globals?.hike_name || globals?.trail_name || 'Hike').trim(),
    shareUrl: pageUrl || '',
    ...(mapStyleUrl ? { mapStyleUrl } : {}),
    // Only non-empty stats are carried: the wizard renders `config.distance || '—'`, so an empty
    // string and an absent key behave the same, and omitting them keeps the payload honest.
    ...(stats?.distance ? { distance: stats.distance } : {}),
    ...(stats?.elevation ? { elevation: stats.elevation } : {}),
    ...(stats?.duration ? { duration: stats.duration } : {}),
  };
}

/* ---------- Post-save trailer prompt ----------
 * When the planner saves a hike it redirects to /user/hikes while the server is still building the
 * hike's content (processing_status: pending -> processing -> completed). The hikes list already
 * polls for that transition; these helpers carry "prompt the trailer wizard for THIS hike" across
 * the redirect (sessionStorage, same tab only) and decide when the list may actually open it. */

export const PENDING_TRAILER_PROMPT_KEY = 'pendingTrailerPromptHike';

export interface PendingTrailerPrompt {
  hikeId: string;
  title: string;
}

/** The sessionStorage payload for a just-saved hike, or null when there's nothing to remember. */
export function pendingTrailerPromptPayload(
  hikeId: string | number | null | undefined,
  title?: string | null,
): string | null {
  const id = hikeId != null ? String(hikeId).trim() : '';
  if (!id) return null;
  return JSON.stringify({ hikeId: id, title: (title || '').trim() });
}

/** Parse what a previous page stored; malformed or empty values read as "no pending prompt". */
export function parsePendingTrailerPrompt(raw: string | null | undefined): PendingTrailerPrompt | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { hikeId?: unknown; title?: unknown };
    const id = parsed?.hikeId != null ? String(parsed.hikeId).trim() : '';
    if (!id) return null;
    return { hikeId: id, title: typeof parsed.title === 'string' ? parsed.title : '' };
  } catch {
    return null;
  }
}

interface TrailForPrompt extends HikeShareStatSource {
  id?: string | number | null;
  name?: string | null;
  processingStatus?: string | null;
  shareableUrl?: string | null;
}

/**
 * The share-wizard config for a pending post-save prompt, or null while the prompt must keep
 * waiting. Null means "not yet": the hike hasn't appeared in the list, or is still
 * pending/processing (a legacy null status counts as done, matching the list's own poll). The
 * trailer needs the processed route geometry, so the wizard must not open sooner.
 */
export function trailerPromptConfig(
  pending: PendingTrailerPrompt | null | undefined,
  trails: ReadonlyArray<TrailForPrompt> | null | undefined,
  fallbackUrl: string,
): {
  hikeId: string;
  title: string;
  shareUrl: string;
  distance?: string;
  elevation?: string;
  duration?: string;
} | null {
  if (!pending?.hikeId) return null;
  const trail = (trails || []).find((t) => t?.id != null && String(t.id) === pending.hikeId);
  if (!trail) return null;
  const status = trail.processingStatus;
  if (status === 'pending' || status === 'processing') return null;
  if (status && status !== 'completed') return null; // failed imports never get a trailer prompt
  // The list row already carries the hike's stats, so the auto-prompt gets the same populated stat
  // row as the flyover's Share button rather than three em-dashes.
  const stats = hikeShareStats(trail);
  return {
    hikeId: pending.hikeId,
    title: (trail.name || pending.title || 'Hike').trim(),
    shareUrl: trail.shareableUrl || fallbackUrl || '',
    ...(stats.distance ? { distance: stats.distance } : {}),
    ...(stats.elevation ? { elevation: stats.elevation } : {}),
    ...(stats.duration ? { duration: stats.duration } : {}),
  };
}
